import React from 'react';
import { X, Mic, Calendar, Clock, Tag, FileText, ListChecks } from 'lucide-react';
import useUtils from '../hooks/useUtils';
import TranscriptTurns from './TranscriptTurns';
import { getMeetingNotes } from '../utils/formatTranscriptDisplay';

const TranscriptModal = ({ showModal, setShowModal, recording }) => {
    const { formatTime } = useUtils();

    if (!showModal || !recording) return null;

    const meetingNotes = getMeetingNotes(recording);

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                            <Mic className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">
                                {recording.name ||
                                    `${recording.category?.charAt(0).toUpperCase() + recording.category?.slice(1)} Recording`}
                            </h2>
                            <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                                <span className="flex items-center">
                                    <Calendar className="w-4 h-4 mr-1" />
                                    {new Date(recording.timestamp).toLocaleDateString()}
                                </span>
                                <span className="flex items-center">
                                    <Clock className="w-4 h-4 mr-1" />
                                    {formatTime(recording.duration || 0)}
                                </span>
                                <span
                                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        recording.status === 'processed'
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-yellow-100 text-yellow-700'
                                    }`}
                                >
                                    {recording.status}
                                </span>
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowModal(false)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                    <div className="space-y-6">
                        {meetingNotes && (
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                                    <FileText className="w-5 h-5 mr-2 text-purple-600" />
                                    Meeting notes
                                </h3>
                                <div className="bg-purple-50/80 border border-purple-100 rounded-xl p-4 space-y-4">
                                    {meetingNotes.summary && (
                                        <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                                            {meetingNotes.summary}
                                        </p>
                                    )}
                                    {meetingNotes.nextSteps?.length > 0 && (
                                        <div>
                                            <p className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                                                <ListChecks className="w-4 h-4 mr-1" />
                                                Next steps
                                            </p>
                                            <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                                                {meetingNotes.nextSteps.map((step, i) => (
                                                    <li key={i}>{step}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Transcript</h3>
                            <div className="bg-gray-50 rounded-xl p-4">
                                <TranscriptTurns recording={recording} />
                            </div>
                        </div>

                        {recording.insights && (
                            <div className="space-y-4">
                                {recording.insights.topics && recording.insights.topics.length > 0 && (
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                                            <Tag className="w-5 h-5 mr-2" />
                                            Key topics
                                        </h3>
                                        <div className="flex flex-wrap gap-2">
                                            {recording.insights.topics.map((topic, index) => (
                                                <span
                                                    key={index}
                                                    className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium"
                                                >
                                                    {topic}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {recording.insights.actionPoints &&
                                    recording.insights.actionPoints.length > 0 && (
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 mb-3">
                                                Action items
                                            </h3>
                                            <div className="space-y-2">
                                                {recording.insights.actionPoints.map((action, index) => (
                                                    <div
                                                        key={index}
                                                        className="bg-white border border-gray-200 rounded-lg p-3"
                                                    >
                                                        <p className="font-medium text-gray-900">{action.text}</p>
                                                        <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-600">
                                                            {action.assignee && (
                                                                <span>Assigned to: {action.assignee}</span>
                                                            )}
                                                            {action.dueDate && (
                                                                <span>
                                                                    Due:{' '}
                                                                    {new Date(action.dueDate).toLocaleDateString()}
                                                                </span>
                                                            )}
                                                            {action.priority && (
                                                                <span
                                                                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                                        action.priority === 'high'
                                                                            ? 'bg-red-100 text-red-700'
                                                                            : action.priority === 'medium'
                                                                              ? 'bg-yellow-100 text-yellow-700'
                                                                              : 'bg-green-100 text-green-700'
                                                                    }`}
                                                                >
                                                                    {action.priority} priority
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TranscriptModal;
